import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useAuth } from "../auth/AuthContext";
import { dashboardApi, type DashParameterGap, type QualityDashboard as QualityDashboardData } from "../api/dashboard";
import { formatDate, formatNumber as brojLokalno } from "../lib/format";
import { localizedName } from "../lib/localizedName";
import {
  BarChart, Donut, HorizontalBars, KpiCard, Muted, Section,
  actionLink, kpiStrip, nazivMeseca, relTime, reviewItem, reviewList, reviewStatusChip,
} from "./dashboard/primitives";

/**
 * The quality control panel, built from what the lab actually measured.
 *
 * The same screen as the sales and procurement panels, read a third time. Sales and procurement count
 * money and documents; this one counts measurements, and it leaves a gap where a pass rate would be,
 * because any pass rate that could be computed would be a claim about the inherited limits, not about
 * the goods.
 *
 * **There is no share „outside limits", and that was measured before it was left out.** The limits from
 * the lab program hang off the article and the customer, not off a product specification, so a naive
 * pass/fail over them measures the limits. On the first screen of the morning such a tile would turn a
 * known flaw in the data into a verdict on quality. In its place stands **how many articles have no
 * limit at all**, because a limit is a hard gate: without one the result form does not open, so that
 * is the number standing between the lab and working in the portal.
 *
 * The charts are carried by the lab archive. The signing queue is carried by the portal, and while it
 * is empty the screen says so openly instead of inventing work.
 */
export default function QualityDashboard() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const { state, hasPermission } = useAuth();
  const canArchive = hasPermission("section:lab_archive:read");
  // Dve kapije, ne jedna: nalaz na verifikaciji i dokument uz LOT daju se zasebno, pa red na
  // potpis postoji ako postoji bilo koja od njih, a servis u njega stavlja samo ono što sme.
  const canQueue =
    hasPermission("section:quality_readings:read") || hasPermission("section:lot_documents:read");

  const [data, setData] = useState<QualityDashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setData(null); setError(null);
    if (canArchive || canQueue) {
      void dashboardApi.quality(12).then((r) => { if (live) setData(r); }).catch((e) => { if (live) setError(e.message); });
    }
    return () => { live = false; };
  }, [canArchive, canQueue]);
  const showArchive = canArchive && !!data && data.archiveVisible !== false;
  const showQueue = canQueue && !!data && data.queueVisible !== false;

  const [period, setPeriod] = useState<3 | 6 | 12>(12);
  const [kindFilter, setKindFilter] = useState<string | null>(null);

  const broj = (n: number) => brojLokalno(n, locale, { maximumFractionDigits: 0 });
  const mesecni = useMemo(
    () => (data?.monthlyAnalyses ?? []).map((m) => ({ label: nazivMeseca(m.label, locale), value: m.value })),
    [data, locale],
  );
  const poVrsti = useMemo(
    () => (data?.byControlPointKind ?? []).map((k) => ({
      label: vrstaNaziv(k.kind, t), value: k.analyses, color: vrstaBoja(k.kind),
    })),
    [data, t],
  );

  if (state.status !== "authenticated") return null;
  const { user } = state;
  const hour = new Date().getHours();
  const timeGreetingKey =
    hour < 12 ? "dashboard.morning" : hour < 18 ? "dashboard.afternoon" : "dashboard.evening";
  const timeGreetingFallback =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const kindTotal = poVrsti.reduce((sum, p) => sum + p.value, 0);
  const gapPct = data?.testsOrdered
    ? Math.round(((data.testsUnmeasured ?? 0) / data.testsOrdered) * 100)
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label={t("dashboard.breadcrumb.aria", "Breadcrumb")}>
            <span>{t("nav.home", "Home")}</span>
            <span>{t("nav.dashboard", "Dashboard")}</span>
          </nav>
          <h1>{t(timeGreetingKey, { defaultValue: timeGreetingFallback })}, {user.firstName}</h1>
          <div className="sub">{t("dashboard.quality.subtitle", "What needs your attention today")}</div>
        </div>
      </div>

      {error && <div className="tag err" role="alert">{error}</div>}
      {!data && !error && <p role="status">{t("common.loading", "Loading…")}</p>}

      <div style={kpiStrip}>
        {/* The archive stops on its last analysis, and the year is counted only that far. The card
            names that day, so the figure is not read as standing for today. */}
        {showArchive && data.analysesYtd !== null && <KpiCard
          delay={0}
          label={t("dashboard.quality.kpi.analysesYtd", "Analyses year to date")}
          value={data?.analysesYtd ?? 0}
          format={broj}
          delta={data?.analysesYtdDeltaPct ?? undefined}
          deltaLabel={data?.analysesYtdDeltaPct == null
            ? t("dashboard.quality.kpi.noBase", "lane nije bilo analiza") : undefined}
          note={data.archiveUntil
            ? t("dashboard.quality.kpi.archiveUntil", "arhiv laboratorije do {{datum}}", {
              datum: formatDate(data.archiveUntil, locale),
            })
            : undefined}
          spark={mesecni.map((m) => m.value)}
          color="var(--brand-primary)"
        />}
        {/* Razmak, ne procenat prolaza. Test naručen pa neizmeren je posao koji je neko poručio i
            niko nije uradio — jedina ocena koju arhiv podnosi bez tvrdnje o granicama. */}
        {showArchive && data.testsUnmeasured !== null && <KpiCard
          delay={1}
          label={t("dashboard.quality.kpi.unmeasured", "Ordered and never measured")}
          value={data?.testsUnmeasured ?? 0}
          format={broj}
          deltaLabel={t("dashboard.quality.kpi.unmeasuredOf", "od {{n}} naručenih testova{{pct}}", {
            n: broj(data?.testsOrdered ?? 0),
            pct: gapPct == null ? "" : ` · ${gapPct}%`,
          })}
          deltaTone={(data?.testsUnmeasured ?? 0) > 0 ? "warn" : "ok"}
          color="var(--warn)"
        />}
        {/* Granica je tvrda kapija: bez nje se obrazac za rezultat ne otvara. Zato ovde stoji broj
            artikala bez granice, a ne udeo van granice — taj bi merio granice, ne robu. */}
        {showArchive && data.articlesWithoutLimit !== null && <KpiCard
          delay={2}
          label={t("dashboard.quality.kpi.noLimit", "Artikala bez granice")}
          value={data?.articlesWithoutLimit ?? 0}
          format={broj}
          deltaLabel={t("dashboard.quality.kpi.noLimitOf", "od {{n}} merenih artikala", {
            n: broj(data?.articlesMeasured ?? 0),
          })}
          deltaTone={(data?.articlesWithoutLimit ?? 0) > 0 ? "warn" : "ok"}
          color="var(--err)"
        />}
      </div>

      {showQueue && <Section
        title={t("dashboard.quality.queue.title", "Waiting for your signature")}
        action={
          <Link to="/quality/awaiting-verification" style={actionLink}>
            {data.signatureQueueSize === 0
              ? t("dashboard.sales.viewAll", "View all")
              : t("dashboard.sales.review.viewAll", "View all ({{count}})", { count: data.signatureQueueSize })} →
          </Link>
        }
      >
        {data.signatureQueue.length === 0 ? (
          <Muted>
            {t("dashboard.quality.queue.empty", "Nothing is waiting on your signature: no reading is held for verification and no lot document is held from the buyer.")}
          </Muted>
        ) : (
          <>
            <ul style={reviewList}>
              {data.signatureQueue.map((s) => {
                const fresh = Date.now() - new Date(s.since).getTime() < 24 * 3600 * 1000;
                const dokument = s.kind === "LOT_DOCUMENT";
                return (
                  <li key={s.id} style={reviewItem}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                      {fresh && (
                        <span
                          className="ord-fresh-dot"
                          aria-label={t("orders.row.freshLabel", "New")}
                          title={t("orders.row.freshTitle", "New — submitted in the last 24h")}
                        />
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {s.title}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--mono)" }}>
                          {s.subtitle ?? "—"}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={reviewStatusChip(dokument ? "review" : "submit")}>
                        {dokument
                          ? t("dashboard.quality.queue.document", "Dokument uz LOT")
                          : t("dashboard.quality.queue.reading", "Nalaz na verifikaciji")}
                      </span>
                      <span style={{ fontSize: 12, color: "var(--ink-3)", whiteSpace: "nowrap" }}>
                        {relTime(s.since, locale, t)}
                      </span>
                      <Link
                        to={dokument ? "/quality/lot-documents" : "/quality/awaiting-verification"}
                        className="btn sm"
                        style={{ textDecoration: "none" }}
                      >
                        {t("dashboard.sales.review.action", "Review")} →
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
            {data.signatureQueueSize > data.signatureQueue.length && (
              <Muted>
                {t("dashboard.quality.queue.cut", "Prikazano {{prikazano}} od {{svih}} stavki.", {
                  prikazano: data.signatureQueue.length,
                  svih: broj(data.signatureQueueSize),
                })}
              </Muted>
            )}
          </>
        )}
      </Section>}

      <div className="dash-two-col">
        {showArchive && <Section
          title={t("dashboard.quality.charts.monthly", "Analiza po mesecima")}
          action={
            <div style={{ display: "flex", gap: 4 }}>
              {[3, 6, 12].map((m) => (
                <button key={m} type="button" className="sd-tab-btn" aria-pressed={period === m}
                        onClick={() => setPeriod(m as 3 | 6 | 12)}>
                  {m}m
                </button>
              ))}
            </div>
          }
        >
          {mesecni.length === 0 ? (
            <Muted>{t("dashboard.quality.charts.noAnalyses", "Nema analiza u ovom periodu.")}</Muted>
          ) : (
            <BarChart data={mesecni.slice(-period)} color="var(--brand-primary)" formatValue={broj} />
          )}
        </Section>}

        {/* Vrsta materijala, ne sama tačka: tačaka je 23 i krug sa 23 odsečka ne čita se.
            Vrsta ih je četiri plus analize bez tačke, i ona odlučuje šta nalaz znači — heksan preko
            granice u otpadnoj vodi je pitanje ispuštanja, isti broj u ulju je pitanje proizvoda. */}
        {showArchive && <Section
          title={t("dashboard.quality.charts.byKind", "Analize po vrsti materijala")}
          action={kindFilter && (
            <button type="button" className="sd-tab-btn" onClick={() => setKindFilter(null)}>
              {t("dashboard.sales.charts.clearFilter", "Clear filter")}
            </button>
          )}
        >
          {poVrsti.length === 0 ? (
            <Muted>{t("dashboard.quality.charts.noAnalysesYear", "Nema analiza u ovoj godini.")}</Muted>
          ) : (
            <Donut
              slices={poVrsti}
              highlight={kindFilter}
              onSliceClick={(label) => setKindFilter(kindFilter === label ? null : label)}
              centerLabel={broj(
                kindFilter ? (poVrsti.find((p) => p.label === kindFilter)?.value ?? 0) : kindTotal,
              )}
              centerSub={kindFilter ?? t("dashboard.quality.charts.analyses", "analiza")}
              formatValue={broj}
            />
          )}
        </Section>}
      </div>

      {/* Tabela na mestu dužnika: red je parametar, a brojka je posao koji je neko poručio i niko
          nije uradio. Naslov obećava onoliko koliko tabela pokazuje. */}
      {showArchive && <Section
        title={t("dashboard.quality.gaps.title", "Najviše naručenih a neizmerenih testova")}
        action={
          <Link to="/quality/lab-archive" style={actionLink}>
            {t("dashboard.sales.viewAll", "Prikaži sve")} →
          </Link>
        }
      >
        {data === null ? (
          <Muted>{t("common.loading", "Učitavanje…")}</Muted>
        ) : data.parameterGaps.length === 0 ? (
          <Muted>{t("dashboard.quality.gaps.empty", "Svaki naručen test je i izmeren.")}</Muted>
        ) : (
          <GapsTable rows={data.parameterGaps} locale={locale} />
        )}
      </Section>}

      <div className="dash-two-col">
        {showArchive && <Section title={t("dashboard.quality.charts.topPoints", "Najopterećenije kontrolne tačke")}>
          {(data?.topControlPoints.length ?? 0) === 0 ? (
            <Muted>{t("dashboard.quality.charts.noPoints", "Nijedna analiza ove godine ne nosi kontrolnu tačku.")}</Muted>
          ) : (
            <HorizontalBars
              data={data?.topControlPoints ?? []}
              max={data?.topControlPoints[0]?.value ?? 1}
              format={broj}
            />
          )}
        </Section>}

        {showArchive && <Section title={t("dashboard.quality.charts.topArticles", "Najviše analizirani artikli")}>
          {(data?.topArticles.length ?? 0) === 0 ? (
            <Muted>{t("dashboard.quality.charts.noArticles", "Nema analiziranih artikala u ovoj godini.")}</Muted>
          ) : (
            <HorizontalBars
              data={data?.topArticles ?? []}
              max={data?.topArticles[0]?.value ?? 1}
              format={broj}
            />
          )}
        </Section>}
      </div>

    </div>
  );
}

/**
 * Naručeno protiv neizmerenog, po parametru.
 *
 * Ista tabela kao spisak dužnika na druge dve table — isti pragovi, ista dugmad za redosled — samo
 * što se ovde boji udeo a ne starost duga. Red nema vezu nikuda: parametar nije dokument, pa bi
 * veza vodila na spisak koji ovaj red ne bi ni našao.
 */
function GapsTable({ rows, locale }: { rows: DashParameterGap[]; locale: string }) {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<"gap" | "share">("gap");
  const udeo = (r: DashParameterGap) => (r.ordered === 0 ? 0 : (r.unmeasured / r.ordered) * 100);
  const sorted = [...rows].sort((a, b) =>
    sortBy === "share" ? udeo(b) - udeo(a) : b.unmeasured - a.unmeasured,
  );
  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 10, fontSize: 11, color: "var(--ink-3)" }}>
        <span style={{ fontWeight: 600, alignSelf: "center" }}>{t("dashboard.sales.debtors.sortLabel", "Poređaj:")}</span>
        <button type="button" className="sd-tab-btn" aria-pressed={sortBy === "gap"} onClick={() => setSortBy("gap")}>
          {t("dashboard.quality.gaps.sortCount", "Broj")}
        </button>
        <button type="button" className="sd-tab-btn" aria-pressed={sortBy === "share"} onClick={() => setSortBy("share")}>
          {t("dashboard.quality.gaps.sortShare", "Udeo")}
        </button>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: "left", color: "var(--ink-3)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>
            <th style={{ padding: "6px 8px" }}>{t("dashboard.quality.gaps.parameter", "Parametar")}</th>
            <th style={{ padding: "6px 8px", textAlign: "right" }}>{t("dashboard.quality.gaps.unmeasured", "Neizmereno")}</th>
            <th style={{ padding: "6px 8px", textAlign: "right" }}>{t("dashboard.quality.gaps.share", "Udeo")}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const pct = Math.round(udeo(r));
            // Isti pragovi kao boje starosti duga na druge dve table: preko pola crveno, preko
            // četvrtine toplo, ispod hladno.
            const tone = pct > 50 ? "var(--err)" : pct > 25 ? "var(--warn)" : "var(--info)";
            const bg = pct > 50 ? "var(--err-bg)" : pct > 25 ? "var(--warn-bg)" : "var(--info-bg)";
            return (
              <tr key={r.parameterId} className="sd-card"
                  style={{ animationDelay: `${i * 40}ms`, borderTop: "1px solid var(--border)" }}>
                <td style={{ padding: "8px", fontWeight: 600, color: "var(--ink)" }}>
                  {localizedName({ name: r.parameterKey, nameI18n: r.nameI18n }, locale)}
                </td>
                <td style={{ padding: "8px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                  {r.unmeasured}
                  <span style={{ color: "var(--ink-4)", fontWeight: 400 }}> / {r.ordered}</span>
                </td>
                <td style={{ padding: "8px", textAlign: "right" }}>
                  <span style={{
                    padding: "2px 8px", borderRadius: 999,
                    background: bg, color: tone, fontSize: 11, fontWeight: 700,
                  }}>
                    {pct} %
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Vrsta materijala na jeziku korisnika.
 *
 * Isti ključevi koje koristi i arhiv laboratorije, pa ista vrsta znači isto na oba ekrana.
 * `NO_POINT` je svoj odsečak i nikad se ne pripaja drugom: 12.979 analiza od 62.078 vođeno je sa
 * testovima biranim jedan po jedan, a skrivanje toga precenilo bi koliko posla ide po planu.
 */
function vrstaNaziv(kind: string, t: TFunction): string {
  if (kind === "NO_POINT") return t("dashboard.quality.kind.noPoint", "Bez kontrolne tačke");
  const par: Record<string, string> = {
    RAW: "Sirovina", SEMI_PRODUCT: "Poluproizvod", PRODUCT: "Proizvod", UTILITY: "Voda i pomoćno",
  };
  return par[kind] ? t(`lab.kind.${kind}`, { defaultValue: par[kind] }) : kind;
}

/** Boja prati put materijala: sirovina hladna, poluproizvod topao, proizvod zelen, voda siva. */
function vrstaBoja(kind: string): string {
  switch (kind) {
    case "RAW": return "var(--info)";
    case "SEMI_PRODUCT": return "var(--warn)";
    case "PRODUCT": return "var(--ok)";
    case "UTILITY": return "var(--tone-cyan)";
    case "NO_POINT": return "var(--ink-4)";
    default: return "var(--ink-3)";
  }
}
